// @ts-nocheck — Phase 1A: legacy code, types added incrementally
import React from "react";
// Phase 3a — window.toast is now a delegation bridge to the new Toast
// primitive. Legacy callers (ForecastItaly bare-name `toast.success(...)`,
// state-storage's `window.toast.warn`) route through here unchanged in
// shape, but render via <ToastViewport /> in App.tsx.
import { toast as newToast } from "../components/ui/Toast";
/* ───────────────────────────────────────────────────────────────────────────
   primitives.jsx — design-system component shells (Phase A4)
   ───────────────────────────────────────────────────────────────────────────
   These are EMPTY SHELLS at this stage. Each component is a thin wrapper that
   either renders its children unchanged or delegates to existing markup.
   They exist so app.jsx can start importing them without behavior changes.

   Phase B will add real implementations one primitive at a time.
   Spec: _snapshots/2026-05-09-design-system/PRIMITIVES.md

   Loaded as <script type="text/babel" src="primitives.jsx"> BEFORE app.jsx.
   No build step — same loading model as model.jsx.
   ─────────────────────────────────────────────────────────────────────────── */

(function () {
  const { useState, useEffect, useRef, useCallback } = React;

  // ─── window.TOKENS — JS mirror of CSS tokens ─────────────────────────────
  // SVG fill="" doesn't reliably resolve var(--*) across browsers. Charts and
  // any code that needs token values at runtime read them from window.TOKENS.
  // We re-read via getComputedStyle on theme change so dark-mode switches work.
  function readTokens() {
    const cs = getComputedStyle(document.documentElement);
    const get = (name) => cs.getPropertyValue(name).trim();
    return {
      // Spacing (numeric px values for inline use)
      space: {
        0: 0, px: 1, "0_5": 2, 1: 4, "1_5": 6, 2: 8, 3: 12, 4: 16,
        5: 20, 6: 24, 8: 32, 10: 40, 12: 48, 16: 64,
      },
      // Font sizes (numeric px values for SVG fontSize attrs)
      fs: {
        caption: 10, "body-xs": 11, "body-sm": 12, body: 13, "body-lg": 14,
        h3: 16, h2: 20, h1: 24, display: 28, "display-lg": 32,
      },
      fw: { regular: 400, medium: 500, semibold: 600, bold: 700 },
      // Color: chart palette (resolved hex strings — works in SVG fill=)
      chart: {
        1: get("--chart-1") || get("--lilac-700") || "#5A3FD9",
        2: get("--chart-2") || get("--proj")      || "#1F8FB3",
        3: get("--chart-3") || get("--good")      || "#2C9F6F",
        4: get("--chart-4") || get("--ae-500")    || "#D9892A",
        5: get("--chart-5") || get("--warn")      || "#C4523F",
        6: get("--chart-6") || "#2BAFA0",
        7: get("--chart-7") || "#C73975",
        8: get("--chart-8") || get("--ink-3")     || "#6B6280",
      },
      // Sequential ramp
      ramp: [
        get("--ramp-1") || "#EBE4FA",
        get("--ramp-2") || "#C7B5F0",
        get("--ramp-3") || "#9B7BFF",
        get("--ramp-4") || "#7C5CFF",
        get("--ramp-5") || "#4A33B0",
      ],
      // Semantic colors (resolved)
      color: {
        textPrimary:   get("--text-primary")   || get("--ink-1") || "#1A1424",
        textSecondary: get("--text-secondary") || get("--ink-2") || "#4A4458",
        textMuted:     get("--text-muted")     || get("--ink-3") || "#6B6280",
        border:        get("--border")         || get("--line")  || "#E8E2EE",
        borderStrong:  get("--border-strong")  || get("--line-2")|| "#D8D2E0",
        accent:        get("--accent")         || get("--lilac-700") || "#5A3FD9",
        success:       get("--success")        || get("--good") || "#2C9F6F",
        warning:       get("--warning")        || get("--ae-500") || "#D9892A",
        danger:        get("--danger")         || get("--warn") || "#C4523F",
        info:          get("--info")           || get("--proj") || "#1F8FB3",
      },
      // Motion (numeric ms for setTimeout / animation API)
      duration: { fast: 120, base: 200, slow: 320 },
      // Z-index (numeric for inline style if absolutely needed)
      z: {
        base: 0, dropdown: 100, sticky: 200, fixed: 300,
        backdrop: 400, modal: 500, popover: 600, tooltip: 700, toast: 800,
      },
      // Breakpoints (numeric for window.matchMedia)
      bp: { sm: 1024, md: 1280, lg: 1440, xl: 1920 },
    };
  }
  window.TOKENS = readTokens();

  // Re-read tokens when the theme flips (dark/light). Watches body class.
  if (typeof MutationObserver !== "undefined") {
    const observer = new MutationObserver(() => {
      window.TOKENS = readTokens();
      window.dispatchEvent(new Event("tokens-updated"));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
  }

  // ─── PRIMITIVE SHELLS ────────────────────────────────────────────────────
  // Each is a pass-through today. Phase B replaces them with real implementations.
  // Calling code can already import these names; behavior stays identical until
  // their respective phase lands.

  // <Button> — Phase B3.
  // Replaces 14 legacy .btn-* classes (.btn-ghost, .btn-bell, .btn-settings,
  // .btn-refresh, .btn-edit-toggle, .btn-theme, .btn-mini, .btn-toggle,
  // .btn-reset-mini, .btn-page, .btn-sound, .btn-danger, .btn-bell--has-unread,
  // .btn-refresh--err) with a single primitive.
  //
  // Props:
  //   size         "sm" (28h) | "md" (36h, default) | "lg" (44h)
  //   variant      "primary" | "secondary" | "ghost" (default) | "danger"
  //   iconOnly     bool — square aspect ratio, used for icon-only buttons
  //                (auto-applies width=height per size)
  //   loading      bool — disables interaction + sets aria-busy. Caller controls
  //                the visual (e.g. spinner) via children.
  //   disabled     bool
  //   active       bool — applies .btn--active for toggle-style buttons
  //                (theme toggle, edit-mode toggle)
  //   tone         optional accent override: "warn" | "success" — used for
  //                refresh-error state, "has-unread" notifications, etc.
  //   className    extra classes (rare). Use for ad-hoc layout (positioning).
  window.Button = function Button({
    size = "md", variant = "ghost", iconOnly = false,
    loading = false, disabled = false, active = false, tone,
    className = "", children, type = "button", ...rest
  }) {
    const cls = [
      "btn",
      `btn--${variant}`,
      `btn--${size}`,
      iconOnly ? "btn--icon" : "",
      active ? "btn--active" : "",
      tone ? `btn--tone-${tone}` : "",
      loading ? "btn--loading" : "",
      className,
    ].filter(Boolean).join(" ");
    return React.createElement("button", {
      type,
      className: cls,
      disabled: disabled || loading,
      "aria-busy": loading || undefined,
      "aria-pressed": (variant === "ghost" && active != null && (rest.role === "switch" || active)) ? active : undefined,
      ...rest,
    }, children);
  };

  // <Card> — Phase B2.
  // Replaces .card / .chart-card / .notes / .table-card--* in section markup.
  // Composition: caller provides children (typically a SectionHeader + body).
  // The primitive owns the wrapper element + visual treatment (elevation, padding).
  // Collapsible behaviour is owned by the call site (open/onToggle), so different
  // sections can collapse independently using their own state.
  //
  // Props:
  //   padding      "comfortable" (default, --space-6) | "compact" (--space-4) | "none"
  //   elevation    "raised" (default, --shadow-sm) | "flat" (border only)
  //   collapsed    bool — applies .ds-card--collapsed for the collapse-style hide
  //                (call site still renders or omits children based on its own state;
  //                this is a visual modifier for sections that animate collapse).
  //   as           HTML element — defaults to "section"
  //   className    extra classes (rare; layout-specific styles like .chart-card live
  //                in the existing CSS and pass through)
  //   children     contents (SectionHeader + body markup)
  window.Card = function Card({
    padding = "comfortable", elevation = "raised",
    collapsed = false, as = "section",
    className = "", children, ...rest
  }) {
    const cls = [
      "ds-card",
      `ds-card--pad-${padding}`,
      `ds-card--elev-${elevation}`,
      collapsed ? "ds-card--collapsed" : "",
      className,
    ].filter(Boolean).join(" ");
    return React.createElement(as, { className: cls, ...rest }, children);
  };

  // <KpiTile> — Phase B2.
  // Replaces 4 status-card--* variants in the KPI strip + the 3 period-scoped
  // tiles in the Month/Qtr/Custom layout. Single composable primitive, structured
  // slots, identical rhythm across every tile.
  //
  // Composition:
  //   ┌─ HEAD ───────────────────────────────────────┐
  //   │ title + (InfoIcon)         (control / pill / sub) │
  //   ├─ HERO ───────────────────────────────────────┤
  //   │ value (large)                                │
  //   │ valueLabel                                   │
  //   │ valueSub (optional, ⊕ accent line)           │
  //   ├─ BODY (custom children, optional) ───────────┤
  //   │ used by Health card for channel rows         │
  //   ├─ ROWS (label / value pairs) ─────────────────┤
  //   │ Target today  |  €120k                        │
  //   │ vs plan       |  +5.7%                        │
  //   └──────────────────────────────────────────────┘
  //
  // Props:
  //   title         ReactNode — title text or composed (e.g. <>Now <InfoIcon tip=.../></>)
  //   pill          ReactNode — right-aligned chip in head (e.g. status-pill)
  //   control       ReactNode — right-aligned interactive control in head
  //                 (e.g. MultiSelect for ARPU channel filter). pill OR control,
  //                 not both — pill takes precedence if both passed.
  //   sub           ReactNode — secondary label in head right (when no pill/control)
  //   value         ReactNode — big number / hero value
  //   valueLabel    ReactNode — caption below the big number
  //   valueSub      ReactNode — optional sub-line accent (⊕ or ⚠ row).
  //                 Caller composes (multiple lines, custom colors, tooltips).
  //   rows          [{label, value, tone?}] — label/value pairs below body.
  //                 label and value can be ReactNodes (compose InfoIcons into label).
  //   accent        "ahead" | "on" | "behind" | "now" | "warn" | etc. — drives
  //                 the .kpi-tile--<accent> class for tone-specific styling.
  //   className     extra classes (rare)
  //   children      arbitrary body content (e.g. Health channels). Rendered
  //                 between hero and rows.
  window.KpiTile = function KpiTile({
    title, pill, control, sub,
    value, valueLabel, valueSub,
    rows, accent, className = "", children, ...rest
  }) {
    const cls = [
      "kpi-tile",
      accent ? `kpi-tile--${accent}` : "",
      className,
    ].filter(Boolean).join(" ");

    const headRight = pill || control || (sub != null ? React.createElement("span", { className: "kpi-tile__sub" }, sub) : null);
    const titleEl = title != null ? React.createElement("span", { className: "kpi-tile__title" }, title) : null;

    return React.createElement("article", { className: cls, ...rest },
      (titleEl || headRight) ? React.createElement("div", { className: "kpi-tile__head" }, titleEl, headRight) : null,
      (value != null || valueLabel != null || valueSub != null) ? React.createElement("div", { className: "kpi-tile__hero" },
        value != null ? React.createElement("div", { className: "kpi-tile__value" }, value) : null,
        valueLabel != null ? React.createElement("div", { className: "kpi-tile__value-label" }, valueLabel) : null,
        valueSub != null ? React.createElement("div", { className: "kpi-tile__value-sub" }, valueSub) : null
      ) : null,
      children,
      Array.isArray(rows) && rows.length > 0 ? React.createElement("div", { className: "kpi-tile__rows" },
        rows.map((r, i) => r == null ? null : React.createElement("div", { key: i, className: "kpi-tile__row" },
          React.createElement("span", { className: "kpi-tile__row-label" }, r.label),
          React.createElement("span", {
            className: `kpi-tile__row-value ${r.tone ? `kpi-tile__row-value--${r.tone}` : ""}`.trim()
          }, r.value)
        ))
      ) : null
    );
  };

  // <Segmented> — Phase B1 (full).
  // Replaces seg-toggle / head__lang / view-toggle / tf-btn implementations.
  //
  // Per-item options:
  //   id          required unique id
  //   label       text or React node (label can change when active, e.g. "Mese · Mag")
  //   icon        optional leading glyph
  //   title       hover tooltip on the item
  //   accent      "accent" | "danger" | "success" | "warning" | "info" — drives
  //               active-bg per-item (scenario picker uses this)
  //   expandable  bool — when active, shows a chevron and the renderDropdown
  //               callback is invoked to render a panel anchored below.
  //               Click on already-active expandable item toggles the panel.
  //   disabled    bool
  //
  // Group props:
  //   value, onChange — selection state (controlled)
  //   expandedId, onToggleExpand — dropdown state (controlled, independent of selection)
  //                                Pass null to close. The primitive itself calls
  //                                onToggleExpand(null) on outside-click + Escape.
  //   renderDropdown(opt)  — returns JSX rendered inside .segmented__dropdown when
  //                          opt.expandable && opt.id === expandedId.
  //   size                 "sm" (28h) | "md" (36h)
  //   variant              "default" — reserved
  //   ariaLabel            describes the group for screen readers
  //
  // Keyboard: ← / → / Home / End move selection per WAI-ARIA radiogroup pattern.
  // Tab moves into/out of the group as a single stop. Escape closes any open
  // dropdown. Outside-click closes any open dropdown.
  window.Segmented = function Segmented({
    value, options = [], onChange,
    expandedId = null, onToggleExpand, renderDropdown,
    size = "md", variant = "default",
    ariaLabel, className = "", title,
    ...rest
  }) {
    const groupRef = useRef(null);
    const idx = options.findIndex(o => o.id === value);
    const hasExpansion = typeof onToggleExpand === "function";

    // Close dropdown on Escape (when something's open).
    useEffect(() => {
      if (!hasExpansion || expandedId == null) return;
      const onKey = (e) => { if (e.key === "Escape") onToggleExpand(null); };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [hasExpansion, expandedId, onToggleExpand]);

    // Close dropdown on outside click.
    useEffect(() => {
      if (!hasExpansion || expandedId == null) return;
      const onDown = (e) => {
        if (!groupRef.current) return;
        if (!groupRef.current.contains(e.target)) onToggleExpand(null);
      };
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }, [hasExpansion, expandedId, onToggleExpand]);

    const moveTo = (newIdx) => {
      if (newIdx < 0 || newIdx >= options.length) return;
      const opt = options[newIdx];
      if (opt.disabled) return;
      onChange(opt.id);
      // Switching items closes any open dropdown unless the new item is itself expandable.
      if (hasExpansion) onToggleExpand(opt.expandable ? opt.id : null);
      const btn = groupRef.current?.querySelectorAll('[role="radio"]')[newIdx];
      btn?.focus();
    };

    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); moveTo(idx + 1 < options.length ? idx + 1 : 0); }
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); moveTo(idx - 1 >= 0 ? idx - 1 : options.length - 1); }
      else if (e.key === "Home") { e.preventDefault(); moveTo(0); }
      else if (e.key === "End")  { e.preventDefault(); moveTo(options.length - 1); }
    };

    const handleItemClick = (opt) => {
      if (opt.disabled) return;
      const isOn = value === opt.id;
      const isExpanded = isOn && opt.expandable && expandedId === opt.id;
      if (isOn && opt.expandable && hasExpansion) {
        // Toggle dropdown when clicking already-active expandable item.
        onToggleExpand(isExpanded ? null : opt.id);
        return;
      }
      if (!isOn) onChange(opt.id);
      if (hasExpansion) onToggleExpand(opt.expandable ? opt.id : null);
    };

    return React.createElement("div", {
      ref: groupRef,
      role: "radiogroup",
      "aria-label": ariaLabel,
      className: `segmented segmented--${size} segmented--${variant} ${className}`.trim(),
      onKeyDown: onKey,
      title: title,
      ...rest,
    }, options.map((opt) => {
      const isOn = value === opt.id;
      const isExpanded = isOn && opt.expandable && expandedId === opt.id;
      const accentClass = opt.accent ? `segmented__item--accent-${opt.accent}` : "";
      const button = React.createElement("button", {
        type: "button",
        role: "radio",
        "aria-checked": isOn,
        "aria-disabled": opt.disabled || undefined,
        "aria-haspopup": opt.expandable ? "menu" : undefined,
        "aria-expanded": opt.expandable ? isExpanded : undefined,
        tabIndex: isOn ? 0 : -1,
        title: opt.title,
        disabled: opt.disabled,
        className: [
          "segmented__item",
          isOn ? "segmented__item--on" : "",
          isExpanded ? "segmented__item--expanded" : "",
          accentClass,
        ].filter(Boolean).join(" "),
        onClick: () => handleItemClick(opt),
      },
        opt.icon ? React.createElement("span", { className: "segmented__icon", "aria-hidden": "true" }, opt.icon) : null,
        opt.label != null ? React.createElement("span", { className: "segmented__label" }, opt.label) : null,
        (isOn && opt.expandable) ? React.createElement("span", { className: "segmented__chev", "aria-hidden": "true" }, "▾") : null
      );
      // Wrap each item so the dropdown can position absolutely beneath it.
      return React.createElement("span", {
        key: opt.id,
        className: "segmented__item-wrap",
      },
        button,
        (isExpanded && renderDropdown) ? React.createElement("div", {
          className: "segmented__dropdown",
          role: "menu",
        }, renderDropdown(opt)) : null
      );
    }));
  };

  // <Input> — Phase B6.
  // Unified input primitive for new feature work (login forms, profile setup,
  // marketing inputs panel). Existing specialized inputs (EditableNumber's edit
  // mode, deals search box, password gate, radio buttons in sound picker)
  // remain in place — they have nuanced behavior + per-component scoping.
  //
  // Props:
  //   size       "sm" | "md" (default) — heights match Button (.btn--sm/md)
  //   kind       "text" | "number" | "password" | "email" | "search" | "tel"
  //   value      controlled value
  //   onChange   (value) => void  OR  (event) => void if rawEvent=true
  //   rawEvent   bool — when true, onChange receives the event instead of unwrapping value
  //   disabled   bool
  //   error      string | null — when set, applies .input--error + aria-invalid
  //   prefix     ReactNode — leading affix (e.g. "€")
  //   suffix     ReactNode — trailing affix (e.g. "%")
  //   className  extra (rare)
  window.Input = function Input({
    size = "md", kind = "text", value, onChange,
    disabled = false, error = null, rawEvent = false,
    prefix, suffix, className = "", ...rest
  }) {
    const handleChange = (e) => {
      if (typeof onChange !== "function") return;
      onChange(rawEvent ? e : e.target.value);
    };
    const inputEl = React.createElement("input", {
      type: kind,
      value: value == null ? "" : value,
      onChange: handleChange,
      disabled,
      className: [
        "input",
        `input--${size}`,
        error ? "input--error" : "",
        (prefix || suffix) ? "input--has-affix" : "",
      ].filter(Boolean).join(" "),
      "aria-invalid": error ? true : undefined,
      "aria-describedby": error ? `${rest.id || ""}-error` : undefined,
      ...rest,
    });
    if (!prefix && !suffix) return inputEl;
    return React.createElement("span", {
      className: `input-wrap input-wrap--${size}`,
    },
      prefix != null ? React.createElement("span", { className: "input__prefix" }, prefix) : null,
      inputEl,
      suffix != null ? React.createElement("span", { className: "input__suffix" }, suffix) : null
    );
  };

  // <Select> — Phase B6. Native <select> styled to match <Input>. For complex
  // dropdowns with custom rendering (e.g. country flags, channel chips) use
  // <MultiSelectDropdown> below or build a bespoke component on the Segmented
  // primitive's renderDropdown pattern.
  window.Select = function Select({
    size = "md", value, options = [], onChange,
    disabled = false, error = null, className = "", ...rest
  }) {
    return React.createElement("select", {
      value: value == null ? "" : value,
      onChange: (e) => onChange?.(e.target.value),
      disabled,
      className: [
        "input",
        `input--${size}`,
        "input--select",
        error ? "input--error" : "",
        className,
      ].filter(Boolean).join(" "),
      "aria-invalid": error ? true : undefined,
      ...rest,
    }, options.map(o =>
      React.createElement("option", { key: o.id, value: o.id, disabled: o.disabled }, o.label)
    ));
  };

  // <Table> — Phase B7.
  // Lightweight wrapper that imposes density rules on a native <table>. Caller
  // composes <thead>/<tbody>/<tr>/<td> as usual; the density modifier sets row
  // height + cell padding via .table--compact / .table--comfortable. The
  // existing complex tables (Forecast table at ~600 lines, Deals table) keep
  // their bespoke selectors but benefit from the density baseline.
  //
  // Props:
  //   density   "compact" (default, ~32px rows) | "comfortable" (~48px rows)
  //   stickyHeader  bool — applies sticky positioning to <thead>
  window.Table = function Table({
    density = "compact", stickyHeader = false,
    className = "", children, ...rest
  }) {
    const cls = [
      "ds-table",
      `ds-table--${density}`,
      stickyHeader ? "ds-table--sticky-header" : "",
      className,
    ].filter(Boolean).join(" ");
    return React.createElement("table", { className: cls, ...rest }, children);
  };

  // <ChartFrame> — Phase B4.
  // Uniform framing for all chart components (GrowthChart, AeChart,
  // MonthFocusChart). The chart itself (SVG drawing) stays in its own component
  // — ChartFrame owns the title row, controls slot, legend row, and padding.
  // This is the cheapest path to "all charts framed identically" without
  // refactoring the SVG drawing logic.
  //
  // Props:
  //   title       ReactNode — chart title (rendered as h3-ish, --fs-body-lg)
  //   subtitle    ReactNode — secondary line under title (--fs-body-sm muted)
  //   controls    ReactNode — right-aligned controls (Segmented, Buttons)
  //   legend      [{label, color, dashed?, hidden?}] — legend swatches below
  //               OR a ReactNode for fully-custom legend
  //   children    the chart content (typically <svg>)
  //   className   extra (rare)
  window.ChartFrame = function ChartFrame({
    title, subtitle, controls, legend, children,
    className = "", ...rest
  }) {
    const renderLegend = () => {
      if (!legend) return null;
      // Custom legend node — render as-is.
      if (React.isValidElement(legend)) return React.createElement("div", { className: "chart-frame__legend" }, legend);
      // Array of {label, color, dashed?, hidden?} → render swatches.
      if (Array.isArray(legend) && legend.length > 0) {
        return React.createElement("div", { className: "chart-frame__legend" },
          legend.map((item, i) => React.createElement("span", {
            key: i,
            className: `chart-frame__legend-item ${item.hidden ? "chart-frame__legend-item--hidden" : ""}`,
          },
            React.createElement("span", {
              className: `chart-frame__swatch ${item.dashed ? "chart-frame__swatch--dashed" : ""}`,
              style: { background: item.color, borderColor: item.color },
            }),
            React.createElement("span", { className: "chart-frame__legend-label" }, item.label)
          ))
        );
      }
      return null;
    };
    return React.createElement("div", { className: `chart-frame ${className}`.trim(), ...rest },
      (title || subtitle || controls) ? React.createElement("div", { className: "chart-frame__head" },
        React.createElement("div", { className: "chart-frame__title-block" },
          title != null ? React.createElement("div", { className: "chart-frame__title" }, title) : null,
          subtitle != null ? React.createElement("div", { className: "chart-frame__subtitle" }, subtitle) : null
        ),
        controls != null ? React.createElement("div", { className: "chart-frame__controls" }, controls) : null
      ) : null,
      React.createElement("div", { className: "chart-frame__body" }, children),
      renderLegend()
    );
  };

  // <Modal> — Phase B5 will absorb Modal + SoundPickerModal + Inputs panel as drawer.
  // For now, the existing components in app.jsx remain canonical.
  window.Modal = function Modal({ size = "sm", open, onClose, children, ...rest }) {
    if (!open) return null;
    return React.createElement("div", {
      className: `modal-backdrop`, onClick: onClose,
    }, React.createElement("div", {
      className: `modal modal--${size}`,
      onClick: (e) => e.stopPropagation(),
      ...rest,
    }, children));
  };

  // <EmptyState> — Phase B8.
  // Replaces 4 ad-hoc empty patterns (.deals-empty-row, .kanban-col__empty,
  // .bell-empty, raw "—" placeholders). Three sizes: inline (single line),
  // compact (mid-size, used in panels), and large (full-section).
  //
  // Props:
  //   icon       ReactNode — optional emoji/icon glyph
  //   title      ReactNode — primary text
  //   message    ReactNode — secondary line(s)
  //   action     ReactNode — optional <Button> (e.g. "Clear filters")
  //   size       "inline" | "compact" | "large" (default)
  window.EmptyState = function EmptyState({
    icon, title, message, action, size = "large", className = "", ...rest
  }) {
    return React.createElement("div", {
      className: `empty-state empty-state--${size} ${className}`.trim(),
      role: "status",
      ...rest,
    }, [
      icon && React.createElement("div", { key: "i", className: "empty-state__icon" }, icon),
      title && React.createElement("div", { key: "t", className: "empty-state__title" }, title),
      message && React.createElement("div", { key: "m", className: "empty-state__msg" }, message),
      action && React.createElement("div", { key: "a", className: "empty-state__action" }, action),
    ].filter(Boolean));
  };

  // Phase 3a — window.toast is now a delegation bridge to the new Toast
  // primitive (src/components/ui/Toast.tsx). Legacy call shapes preserved:
  //   window.toast.warn(message, title) → newToast.warn({ title, message })
  //   window.toast.error(message, title) → newToast.danger(...)    (rename)
  //   window.toast.show({ variant, title, message, duration }) → newToast.X
  //
  // The single <ToastViewport /> in App.tsx renders everything. The legacy
  // ToastProvider is kept as an exported component for back-compat with
  // anything still rendering it, but it's a no-op (App.tsx no longer mounts
  // it; ToastViewport replaced it). New code should import { toast } from
  // "@/components/ui/Toast" directly — the window bridge is back-compat.
  // Defensive coercion — legacy callers occasionally pass non-string args
  // (an options object as `title` was tripped during the Phase 4b regression
  // walk; the resulting "Objects are not valid as a React child" error
  // cascades to ErrorBoundary and blanks the page). Coerce both fields to
  // string at the boundary so a misuse degrades to a readable toast instead
  // of a full crash. null/undefined keep their semantic meaning (skip the
  // field) — only actual non-string values get serialized.
  const safeStr = (v) => {
    if (v == null) return v;                       // null/undefined → pass through (Toast handles missing fields)
    if (typeof v === "string") return v;           // string → unchanged
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    try { return JSON.stringify(v); } catch { return String(v); }
  };
  function bridgeShow({ variant = "info", title, message, duration }) {
    const tone = variant === "error" ? "danger"
              : variant === "warning" ? "warn"
              : variant; // "info"|"success" pass through
    const fn = newToast[tone] || newToast.info;
    return fn({ title: safeStr(title), message: safeStr(message), duration });
  }
  window.toast = {
    show:    bridgeShow,
    dismiss: (id) => newToast.dismiss(id),
    success: (msg, title) => newToast.success({ title: safeStr(title), message: safeStr(msg) }),
    error:   (msg, title) => newToast.danger ({ title: safeStr(title), message: safeStr(msg), duration: 6000 }),
    info:    (msg, title) => newToast.info   ({ title: safeStr(title), message: safeStr(msg) }),
    warn:    (msg, title) => newToast.warn   ({ title: safeStr(title), message: safeStr(msg) }),
  };
  // No-op kept so window.ToastProvider exists (legacy callers may
  // conditionally check for it). The actual UI lives in <ToastViewport /> in
  // App.tsx; rendering this is harmless and produces nothing.
  window.ToastProvider = function ToastProvider() { return null; };

  // ─── Marker so callers know primitives are loaded ──────────────────────────
  window.__PRIMITIVES_VERSION = "0.1-shells";
})();

// ─── Phase 4 sub-step 10b: ESM exports ─────────────────────────────────────
// The IIFE above is the legacy load path (publishes window.X for any
// bare-name JSX usage that's already in the codebase — ForecastItaly + a
// few composed files still rely on globals). New / migrated code should
// import explicitly from this module instead.
//
// These exports run AFTER the IIFE has executed (module top-level
// statements run top-to-bottom), so the W.X references are bound to the
// real functions, not undefined.
//
// TOKENS is intentionally exposed as `getTokens()` rather than a const:
// the MutationObserver inside the IIFE reassigns window.TOKENS on theme
// flips. Anyone importing a snapshot would see stale values after the
// next dark/light switch. Call getTokens() each time you need the
// current values; in render code this happens naturally per render.

declare global {
  interface Window {
    TOKENS: any;
    Button: any;
    Card: any;
    KpiTile: any;
    Segmented: any;
    Input: any;
    Select: any;
    Table: any;
    ChartFrame: any;
    Modal: any;
    EmptyState: any;
    toast: any;
    ToastProvider: any;
    __PRIMITIVES_VERSION: string;
  }
}

const W = window as any;

export const Button: any        = W.Button;
export const Card: any          = W.Card;
export const KpiTile: any       = W.KpiTile;
export const Segmented: any     = W.Segmented;
export const Input: any         = W.Input;
export const Select: any        = W.Select;
export const Table: any         = W.Table;
export const ChartFrame: any    = W.ChartFrame;
export const Modal: any         = W.Modal;
export const EmptyState: any    = W.EmptyState;
export const toast: any         = W.toast;
export const ToastProvider: any = W.ToastProvider;

/** Always returns the current tokens object — safe across theme switches. */
export function getTokens(): any { return W.TOKENS; }
